import { TaskObjectNaming } from "@jugl-web/rest-api/tasks";
import {
  InteractiveContainer,
  PlainButton,
  Tooltip,
} from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { FC } from "react";
import { ReactComponent as ChevronRightIcon } from "./assets/chevron-right.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ExpandIcon } from "./assets/expand.svg";
import { ReactComponent as TaskIcon } from "./assets/task.svg";

export type TimeTrackedPopoverHeaderTypeSpecificProps =
  | { type: "basic" }
  | { type: "expandable" }
  | {
      type: "with-task-name";
      taskObjectNaming: TaskObjectNaming;
      taskName: string;
      onNavigateToTask: () => void;
    };

export type TimeTrackedPopoverHeaderProps =
  TimeTrackedPopoverHeaderTypeSpecificProps & {
    onShowAll: () => void;
    onClose: () => void;
  };

export const TimeTrackedPopoverHeader: FC<TimeTrackedPopoverHeaderProps> = ({
  onShowAll,
  onClose,
  ...props
}) => {
  const { t } = useTranslations();

  return (
    <div className="flex max-w-[500px] min-w-[336px] items-center justify-between border border-t-0 border-r-0 border-l-0 border-solid border-dark-100 px-4 py-2">
      {/* Left side */}
      {props.type === "basic" || props.type === "expandable" ? (
        <span className="font-secondary text-sm text-dark-800">
          {t({ id: "tasks-page.time-tracker", defaultMessage: "Time Tracker" })}
        </span>
      ) : (
        <Tooltip
          placement="bottom"
          renderTrigger={({ ref: tooltipRef, props: tooltipProps }) => (
            <InteractiveContainer
              ref={tooltipRef}
              className="group -ml-1 flex items-center gap-2 overflow-hidden rounded-lg p-1 transition-colors hover:bg-grey-100"
              onClick={() => {
                props.onNavigateToTask();
                onClose();
              }}
              {...tooltipProps}
            >
              <TaskIcon className="shrink-0" />
              <span className="truncate font-secondary text-[15px] font-medium text-dark-900">
                {props.taskName}
              </span>
              <ChevronRightIcon className="ml-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
            </InteractiveContainer>
          )}
        >
          {t(
            {
              id: "task-preview-sidebar.expand-icon-title",
              defaultMessage: "Go to {singularTaskObjectName} details",
            },
            { singularTaskObjectName: props.taskObjectNaming.singular }
          )}
        </Tooltip>
      )}
      {/* Right side */}
      {props.type === "basic" || props.type === "with-task-name" ? (
        <PlainButton
          className="rounded transition-colors hover:bg-grey-100"
          onClick={onClose}
        >
          <CloseIcon />
        </PlainButton>
      ) : (
        <Tooltip
          placement="bottom"
          renderTrigger={({ ref: tooltipRef, props: tooltipProps }) => (
            <PlainButton
              ref={tooltipRef}
              className="rounded transition-colors hover:bg-grey-100"
              onClick={() => {
                onShowAll();
                onClose();
              }}
              {...tooltipProps}
            >
              <ExpandIcon />
            </PlainButton>
          )}
        >
          {t({
            id: "tasks-page.see-detailed-info",
            defaultMessage: "See detailed info",
          })}
        </Tooltip>
      )}
    </div>
  );
};
